import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiLinkedin, FiMail, FiDownload } from 'react-icons/fi';

const HomeContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 0 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  padding: 0 20px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
    padding: 0 15px;
  }
  
  @media (max-width: 480px) {
    gap: 2rem;
    padding: 0 10px;
  }
`;

const TextContent = styled(motion.div)`
  color: white;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const ImageContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    order: 1;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    max-width: 350px;
  }
`;

const Name = styled(motion.h1)`
  font-family: 'Playfair Display', serif;
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Title = styled(motion.h2)`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #e2e8f0;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: #f7fafc;
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: 100%;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.8rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  min-height: 48px;
  min-width: 120px;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  
  &.primary {
    background: rgba(255, 255, 255, 0.9);
    color: #667eea;
    
    &:hover {
      background: white;
      color: #667eea;
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem 1.5rem;
    font-size: 0.9rem;
    width: 100%;
    max-width: 280px;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  
  &.shape1 {
    width: 100px;
    height: 100px;
    top: 10%;
    right: 10%;
  }
  
  &.shape2 {
    width: 150px;
    height: 150px;
    bottom: 20%;
    left: 5%;
  }
  
  &.shape3 {
    width: 80px;
    height: 80px;
    top: 60%;
    right: 30%;
  }
`;

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 180, 360],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <HomeContainer>
      <FloatingElements>
        <FloatingShape 
          className="shape1"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '0s' }}
        />
        <FloatingShape 
          className="shape2"
          variants={floatingVariants}
          animate="animate" 
          style={{ animationDelay: '2s' }}
        />
        <FloatingShape 
          className="shape3"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '4s' }}
        />
      </FloatingElements>

      <ContentWrapper>
        <TextContent
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Name variants={itemVariants}>
            Shardul Tillu
          </Name>
          <Title variants={itemVariants}>
            MBA Finance & Business Analytics
          </Title>
          <Description variants={itemVariants}>
            Highly motivated and adaptable business professional with experience across operations, client services, IT coordination,
            sales support, and hospitality environments. Known for being a fast learner, self-starter, and proactive contributor who
            quickly understands new systems, processes, and business contexts. Tech-savvy and detail-oriented, with a strong
            willingness to learn new tools, industries, and responsibilities while consistently delivering value to teams and
            stakeholders. Brings a growth mindset, strong work ethic, and a genuine desire to contribute beyond assigned
            responsibilities.
          </Description>
          <ButtonGroup variants={itemVariants}>
            <Button 
              href="/contact" 
              className="primary"
            >
              <FiMail />
              Get in Touch
            </Button>
            <Button 
              href="/resume.docx" 
              download
            >
              <FiDownload />
              Download Resume
            </Button>
            <Button 
              href="https://www.linkedin.com/in/shardul-tillu/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <FiLinkedin />
              LinkedIn
            </Button>
          </ButtonGroup>
        </TextContent>

        <ImageContainer
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <ProfileImage 
            src="/shardul-profile.png" 
            alt="Shardul Tillu - Professional Portrait"
            onError={(e) => {
              // Fallback to a placeholder if image fails to load
              e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDUwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI1MDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjZjdmYWZjIi8+CjxjaXJjbGUgY3g9IjI1MCIgY3k9IjE4MCIgcj0iODAiIGZpbGw9IiNkZGQ2ZmUiLz4KPHBhdGggZD0iTTEwMCA0NDBjMC04MCA4MC0xNDAgMTUwLTE0MHMxNTAgNjAgMTUwIDE0MHYxNjBIMTAwVjQ0MFoiIGZpbGw9IiNkZGQ2ZmUiLz4KPHRleHQgeD0iMjUwIiB5PSI1NDAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzY2N2VlYSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U2hhcmR1bCBUaWxsdTwvdGV4dD4KPC9zdmc+";
            }}
          />
        </ImageContainer>
      </ContentWrapper>
    </HomeContainer>
  );
};

export default Home;